'use client'

import { useTranslation } from '@/lib/i18n/useTranslation.client'
import { type TrackingGaEvent } from '@/lib/tracking/services/tracking.eventTypes'
import { Card } from '@/ui/cards/card/Card'
import { Icon } from '@/ui/icons/icon/Icon'
import { Picture } from '@/ui/media/picture/Picture.client'
import { type FullOpeningHourFragment } from '@gql-generated/graphql'
import { getPointOfSalePicture } from '../../services/pointOfSale.service'
import { PointOfSaleOpeningStatus } from '../point-of-sale-opening-status/PointOfSaleOpeningStatus.client'

import { Link } from '@/ui/links/link/Link.client'

export type PointOfSaleStatusCardWrapperProps = {
  pointOfSaleName?: string | null
  pointOfSaleImage?: string | null
  openingHours?: FullOpeningHourFragment[] | null
  exceptionalDates?: Array<{ date?: string | null; isClosed: boolean }> | null
  phoneNumber?: string | null
  link?: string | null
  themeFallbackImage?: string
  trackingData?: {
    phone?: TrackingGaEvent
    location?: TrackingGaEvent
  }
}

export function PointOfSaleStatusCardContent({
  pointOfSaleName,
  pointOfSaleImage,
  openingHours,
  exceptionalDates,
  phoneNumber,
  link,
  themeFallbackImage,
  trackingData,
}: PointOfSaleStatusCardWrapperProps) {
  const { t } = useTranslation(['pointOfSaleStatusCard'])
  const image = getPointOfSalePicture({
    cmsImageUrl: pointOfSaleImage,
    themeFallbackImage,
    alt: pointOfSaleName || t('pointOfSaleStatusCard:myPointOfSale'),
  })

  return (
    <div data-testid="point-of-sale-status-card" className="w-[20.5rem] min-w-[20.5rem]">
      <Card
        padding="none"
        radius="sm"
        skin="brand-1-light"
        className="flex h-15 w-full flex-row items-center justify-between gap-2 px-3"
      >
        <Picture
          image={image}
          className="size-8 overflow-hidden rounded-full"
          width="64"
          height="64"
        />

        <div className="flex grow flex-col justify-center">
          <p
            data-testid="point-of-sale-status-card/title"
            className="mb-1 line-clamp-2 text-lg font-bold uppercase"
          >
            {t('pointOfSaleStatusCard:myPointOfSale')}
          </p>

          {pointOfSaleName && (
            <div className="flex flex-col gap-0.5">
              <p className="line-clamp-1 font-bold">{pointOfSaleName}</p>
              {openingHours && (
                <PointOfSaleOpeningStatus
                  openingHours={openingHours}
                  exceptionalDates={exceptionalDates || []}
                />
              )}
            </div>
          )}
        </div>

        <div className="flex flex-col gap-1">
          {phoneNumber && (
            <Link
              bold
              variant="button-secondary"
              isRound
              iconLeft={<Icon.Phone />}
              href={`tel:${phoneNumber}`}
              opensInNewTab
              trackingData={trackingData?.phone}
            >
              {t('pointOfSaleStatusCard:callCurrentPointOfSaleButton')}
            </Link>
          )}

          {link && (
            <Link
              bold
              variant="button-secondary"
              isRound
              iconLeft={<Icon.MapPin />}
              href={link}
              opensInNewTab
              trackingData={trackingData?.location}
            >
              {t('pointOfSaleStatusCard:itineraryToCurrentPointOfSaleButton')}
            </Link>
          )}
        </div>
      </Card>
    </div>
  )
}
